// Embed mode: the app framed on another site by /embed.js (served by the API), which loads
// /?src=<source>&embed=1 in an iframe and sizes the iframe to the height we report.

const FLAG = "gp_embed";

function framed(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    // Reading window.top across origins can throw: that means we're framed.
    return true;
  }
}

/** True inside an iframe, or when opened with embed=1 (remembered for this tab's session). */
export function isEmbedded(search: string = window.location.search): boolean {
  if (new URLSearchParams(search).get("embed") === "1") {
    try {
      sessionStorage.setItem(FLAG, "1");
    } catch {
      // ignore
    }
    return true;
  }
  if (framed()) return true;
  try {
    return sessionStorage.getItem(FLAG) === "1";
  } catch {
    return false;
  }
}

export const HEIGHT_MESSAGE = "gifts-profile:height";

/**
 * Tells the parent page our height on every layout change (ResizeObserver on <body>,
 * at most once per frame). Returns a stop function.
 */
export function startHeightReporter(target: Window | null = window.parent): () => void {
  if (!target || target === window || typeof ResizeObserver === "undefined") return () => {};
  let frame = 0;
  let last = -1;
  const send = () => {
    frame = 0;
    // The body's own height, not documentElement.scrollHeight: inside an iframe that never
    // drops below the frame's current height, so the frame could grow but never shrink.
    const height = Math.ceil(document.body.getBoundingClientRect().height);
    if (height === last) return;
    last = height;
    target.postMessage({ type: HEIGHT_MESSAGE, height }, "*");
  };
  const schedule = () => {
    if (!frame) frame = requestAnimationFrame(send);
  };
  const ro = new ResizeObserver(schedule);
  ro.observe(document.body);
  schedule();
  return () => {
    ro.disconnect();
    if (frame) cancelAnimationFrame(frame);
  };
}
